import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, ClipboardList, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { inventoryApi } from "./inventoryApi";
import { REQUEST_STATUSES, REQUEST_PRIORITIES, PRIORITY_MAP } from "./procurementConstants";
import { formatDateTime } from "./inventoryConstants";
import { RequestFormModal } from "./RequestFormModal";
import { RequestDetailModal } from "./RequestDetailModal";

export const RequestsSection = ({ currentUser, canManage, canCreate = true, vehicles }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [vendors, setVendors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      setRequests(await inventoryApi.getRequests(params));
    } catch { toast.error("Failed to load requests"); }
    setLoading(false);
  }, [statusFilter, priorityFilter]);

  useEffect(() => { inventoryApi.getVendors().then(setVendors).catch(() => {}); }, []);
  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  return (
    <div className="space-y-4" data-testid="requests-section">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap gap-2">
          <select className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="request-status-filter">
            <option value="">All Statuses</option>
            {Object.entries(REQUEST_STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} data-testid="request-priority-filter">
            <option value="">All Priorities</option>
            {REQUEST_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        {canCreate && <Button onClick={() => setShowForm(true)} data-testid="new-request-btn"><Plus className="w-4 h-4 mr-1" /> New Request</Button>}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : requests.length === 0 ? (
        <div className="text-center py-12 text-slate-400"><ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No parts requests</p></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {requests.map((r) => {
            const st = REQUEST_STATUSES[r.status] || {};
            const pr = PRIORITY_MAP[r.priority] || {};
            return (
              <button key={r.id} onClick={() => setDetailId(r.id)} data-testid={`request-card-${r.id}`} className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md hover:border-blue-200 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{r.request_number}</span>
                    {r.priority === "truck_down" && <AlertTriangle className="w-4 h-4 text-red-500" />}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.bg} ${st.color}`}>{st.label}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${pr.bg} ${pr.color}`}>{pr.label}</span>
                  {r.unit_number && <span className="text-xs text-slate-500">Unit {r.unit_number}</span>}
                </div>
                {r.description && <p className="text-sm text-slate-600 mt-2 line-clamp-1">{r.description}</p>}
                <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
                  <span>{r.line_items?.length || 0} line item(s)</span>
                  <span>{formatDateTime(r.created_at)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <RequestFormModal open={showForm} onClose={() => setShowForm(false)} vehicles={vehicles} currentUser={currentUser} onSaved={fetchRequests} />
      <RequestDetailModal requestId={detailId} onClose={() => setDetailId(null)} onChanged={fetchRequests} canManage={canManage} vendors={vendors} currentUser={currentUser} />
    </div>
  );
};
